import React, { useEffect, useState } from "react";
import { ShieldCheck, Lock, Sparkles, CheckCircle2 } from "lucide-react";
import { SmartLinkLogoMark } from "../ui/SmartLinkLogoMark";

export const RouteLoadingFallback: React.FC<{ message?: string; title?: string }> = ({
  message = "Loading experience...",
  title = "SmartLink Sovereign Portal",
}) => {
  const [progress, setProgress] = useState(30);

  useEffect(() => {
    const timer1 = setTimeout(() => setProgress(65), 180);
    const timer2 = setTimeout(() => setProgress(90), 400);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <div className="min-h-[420px] w-full flex items-center justify-center p-4 text-center animate-fadeIn">
      <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-5 text-center space-y-4">
        {/* Animated SmartLink Logo Mark Badge with Ping */}
        <div className="relative w-14 h-14 mx-auto flex items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-[#0F2D5C]/15 dark:bg-blue-500/20 animate-ping opacity-60" />
          <div className="relative p-2.5 rounded-xl bg-white dark:bg-slate-800 shadow-md border border-slate-200 dark:border-slate-700 flex items-center justify-center">
            <SmartLinkLogoMark size="sm" animating={progress < 100} />
          </div>
        </div>

        {/* Header */}
        <div className="space-y-1 text-center">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#0F2D5C]/10 text-[#0F2D5C] dark:bg-blue-900/40 dark:text-blue-300 text-[10px] font-bold tracking-wide uppercase">
            <Sparkles className="h-2.5 w-2.5" />
            <span>AUTHENTICATED GATEWAY</span>
          </div>
          <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight">
            {title}
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1 font-medium">
            <Lock className="h-3 w-3 text-[#0F2D5C] dark:text-blue-400 shrink-0" />
            <span>Encrypted Federal &amp; Provider Hub</span>
          </p>
        </div>

        {/* Progress Container */}
        <div className="space-y-2 p-3 bg-slate-50 dark:bg-slate-800/70 rounded-xl border border-slate-200/80 dark:border-slate-700/80 text-left">
          <div className="flex items-center justify-between text-[11px] font-bold text-slate-700 dark:text-slate-200">
            <span className="flex items-center gap-1.5 text-[#0F2D5C] dark:text-blue-400">
              {progress >= 100 ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <SmartLinkLogoMark size="xs" animating={true} />
              )}
              <span>{message}</span>
            </span>
            <span className="font-mono text-[11px] font-extrabold text-[#0F2D5C] dark:text-blue-300">
              {progress}%
            </span>
          </div>

          <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full overflow-hidden">
            <div
              className="bg-[#0F2D5C] dark:bg-blue-600 h-full transition-all duration-300 ease-out rounded-full shadow-sm"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Guarantee */}
        <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400 font-medium">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span>256-Bit SSL Encrypted Session &bull; 100% Uptime Hub</span>
        </div>
      </div>
    </div>
  );
};

export default RouteLoadingFallback;
